/**
 * Générateur — Automatismes : additionner (1213), soustraire (1214),
 * multiplier (1215) et diviser (1216) des fractions.
 * Attendu ID : 1213 | Niveau : 3ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 728 :
 * « Additionner, soustraire, multiplier et diviser des fractions. »).
 * UN FICHIER, QUATRE ATTENDUS (base du prof du 13/09, modèle 1246/1247/1248) :
 * les clés somme_* portent l'attendu 1213, difference_* 1214, produit_* 1215,
 * quotient_* 1216 ; gen_1214 / gen_1215 / gen_1216 sont des dispatchs sur ces
 * clés (genererParmi). La mécanique (tirages, distracteurs, sacrifiés) est
 * commune : une opération = un attendu, trois cartes par opération.
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Les quatre opérations sur DEUX fractions positives, une opération par
 * item, résultat en UNE fraction. Le ± est l'entretien de 1097 (cas de 5e
 * de 1054 et cas « un nombre des deux tables ») ; le × et le ÷ sont le neuf
 * (objectifs 4e l. 663 et 667, automatisés en 3e). Résultat IRRÉDUCTIBLE
 * PAR CONSTRUCTION : simplifier n'est jamais nécessaire (c'est l'objectif
 * 3e l. 731), une seule écriture juste.
 * Frontières : numérateurs négatifs (l. 661-662, choix mode auto : positifs
 * seulement, À ARBITRER), plusieurs opérations (l. 668, gen_147), problèmes
 * (l. 733), le dénominateur commun seul (1223, l. 787).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NC7 /
 * 5NC8 options 4e « et simplifier » (dén. 2..10), 4NC3 / 4NC4 (num. 1..12,
 * dén. 2..12, « donner la fraction irréductible ou l'entier égal ») : ici
 * numérateurs 1..9, dénominateurs 2..9, opérandes > 1 admis en × et ÷
 * (À ARBITRER), résultat jamais entier, termes ≤ 81.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (<op>_flash_texte_trou_saisie) : quatre égalités
 *     de la MÊME opération, deux cases (numérateur, dénominateur) par
 *     résultat ; le ± tire les cas de 1054/1097 (quelconque, multiples,
 *     premiers, même dénominateur), le × et le ÷ leurs items propres.
 *   Carte 2 « Quel résultat ? » (<op>_resultat_qcm_unique) : QUATRE choix,
 *     contenu.sousType = l'opération ; ± : les familles de 1054/1097 ; × :
 *     produits_en_croix, meme_denominateur_garde, hauts_seuls,
 *     bas_additionnes ; ÷ : pas_inverse, inverse_du_mauvais, les_deux_inverses.
 *   Carte 3 « Vrai ou faux » (<op>_sens_tableau_vf) : quatre lignes de la
 *     même opération vérifiées EN VALEUR ; en ÷ la quatrième est la ligne du
 *     geste (« 3/4 ÷ 2/5 = 3/4 × 5/2 » V ; « 2/3 ÷ 5 = 2/3 × 5 » F).
 *
 * INVARIANTS : × : a/b × c/d, pgcd(a, d) = pgcd(c, b) = 1 (produit
 * irréductible), a/b et c/d irréductibles non entières ; ÷ : pgcd(a, c) =
 * pgcd(b, d) = 1 (quotient ad/bc irréductible) ; chaque résultat
 * re-calculé en entiers et vérifié irréductible (throw) ; tout distracteur
 * strictement positif, différent de la clé et des autres EN VALEUR.
 * SACRIFIÉS (fiche d'aide « un cas par page ») : ceux de 1054 et 1097 pour
 * le ± (importés) ; (2/3 ; 4/5) en × ; (3/4 ; 2/5) en ÷ et (3/4 ; 5/2) en ×,
 * dans les deux ordres, auto-testés par gen1213.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP test de positionnement 2de 2025) ────
 *   ± : bas_additionnes (q.8 « 4/5 − 1/3 » → 3/2 : 23,3 %, réussite 63,9 %),
 *       pas_converti, grand_denominateur, voisin_meme_table (1054/1097) ;
 *   × : produits_en_croix PRINCIPALE (q.3 « 2/5 × 2/3 » → 6/10 : 7,6 %,
 *       réussite 84,2 %) ; meme_denominateur_garde (→ 60/15 : 6,3 %) ;
 *       hauts_seuls (2/5 × 3/5 → 6/5, inférence) ; bas_additionnes ;
 *   ÷ (aucun item DEPP) : pas_inverse PRINCIPALE (ac/bd) ; inverse_du_mauvais
 *       (bc/ad) ; les_deux_inverses (bd/ac).
 *   Attention : « produits en croix » EST la clé en division.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';
import {
    tirerItem, fracTex, exprTex as exprTexPlus, resTex, memeValeur,
    distracteursQcm, affirmation, candidats, gcd,
} from '../../5eme/nombres-rationnels/gen_1054';
import {
    tirerQuelconque, distracteursQcmQuelconque, candidatsParFamille, EXPLICATIONS as EXPLICATIONS_1097,
} from '../../4eme/nombres-rationnels/gen_1097';

const ATTENDU_ID = 1213;
/** Une opération = un attendu (base du prof, 13/09). */
export const ATTENDU = { somme: 1213, difference: 1214, produit: 1215, quotient: 1216 };
const OP_DE = { somme: '+', difference: '-', produit: '×', quotient: '÷' };
const opts = (attendu, label) => ({ niveau: '3eme', matiere: 'maths', attendu, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quel résultat ?';
const L_VF = 'Vrai ou faux';

// ─── Le domaine du × et du ÷ ───────────────────────────────────────────────

/** Les exemples de la fiche, sacrifiés dans les deux ordres : (2/3 ; 4/5) en ×, (3/4 ; 2/5) en ÷ et (3/4 ; 5/2) en ×. */
export const SACRIFIES = {
    produit: [[{ a: 2, b: 3 }, { a: 4, b: 5 }], [{ a: 3, b: 4 }, { a: 5, b: 2 }]],
    quotient: [[{ a: 3, b: 4 }, { a: 2, b: 5 }]],
};
const memeFrac = (f, g) => f.a === g.a && f.b === g.b;
const estSacrifie = (cas, g, d) => SACRIFIES[cas].some(([u, v]) => (memeFrac(g, u) && memeFrac(d, v)) || (memeFrac(g, v) && memeFrac(d, u)));

/** Une fraction irréductible, non entière : a 1..9, b 2..9. */
const fractionLibre = () => {
    for (let k = 0; k < 60; k++) {
        const b = randInt(2, 9);
        const a = randInt(1, 9);
        if (a % b !== 0 && gcd(a, b) === 1) return { a, b };
    }
    return { a: 2, b: 3 };
};

/** Le résultat exact d'un × ou d'un ÷, vérifié irréductible et non entier (throw sinon). */
const resultat = (cas, g, d) => {
    const num = cas === 'produit' ? g.a * d.a : g.a * d.b;
    const den = cas === 'produit' ? g.b * d.b : g.b * d.a;
    if (gcd(num, den) !== 1 || den === 1) throw new Error(`gen_1213 : résultat réductible ${num}/${den}`);
    return { num, den };
};

/** Un item { cas, op, g, d, res } de × ou ÷ ; null si 200 essais échouent. */
const tirerMulDiv = (cas) => {
    for (let essai = 0; essai < 200; essai++) {
        const g = fractionLibre();
        const d = fractionLibre();
        if (memeFrac(g, d) || estSacrifie(cas, g, d)) continue;
        const ok = cas === 'produit' ? gcd(g.a, d.b) === 1 && gcd(d.a, g.b) === 1 : gcd(g.a, d.a) === 1 && gcd(g.b, d.b) === 1;
        if (!ok) continue;
        const res = resultat(cas, g, d);
        if (res.num > 81 || res.den > 81) continue;
        return { cas, op: cas === 'produit' ? '×' : '÷', g, d, res };
    }
    return null;
};

// ─── Écritures ────────────────────────────────────────────────────────────

const estPlus = (it) => it.op === '+' || it.op === '-';
const OP_TEX = { '×': '\\times', '÷': '\\div' };
const fTex = (f) => fracTex(f.a, f.b);
/** L'expression KaTeX d'un item, ± par 1054, × et ÷ ici. */
const exprTex = (it) => (estPlus(it) ? exprTexPlus(it) : `${fTex(it.g)} ${OP_TEX[it.op]} ${fTex(it.d)}`);
const ligneFlash = (it) => `${exprTex(it)} = \\dfrac{{{${it.res.num}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${it.res.den}}}}`;
const phraseFlash = (items) => [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');

const EXPLICATIONS = {
    ...EXPLICATIONS_1097,
    produits_en_croix: 'Pour multiplier, c\'est haut fois haut et bas fois bas : 2/5 × 2/3 = 4/15. Les produits en croix, c\'est pour diviser.',
    meme_denominateur_garde: 'Pas de dénominateur commun pour multiplier : haut fois haut, bas fois bas.',
    hauts_seuls: 'Les dénominateurs se multiplient aussi : 2/5 × 3/5 = 6/25, pas 6/5.',
    pas_inverse: 'Diviser par une fraction, c\'est multiplier par son inverse : 3/4 ÷ 2/5 = 3/4 × 5/2.',
    inverse_du_mauvais: 'C\'est la fraction par laquelle on divise qui se retourne, pas la première.',
    les_deux_inverses: 'Une seule fraction se retourne : celle par laquelle on divise.',
    geste_division: 'Diviser par 5, c\'est multiplier par 1/5 ; diviser par 2/5, c\'est multiplier par 5/2.',
    regle: 'Plus ou moins : le même dénominateur d\'abord. Fois : haut fois haut, bas fois bas. Divisé : je multiplie par l\'inverse.',
};

// ─── Distracteurs du × et du ÷ ────────────────────────────────────────────

/** Les candidats d'un item × ou ÷, chacun avec sa valeur exacte, positifs, jamais la clé, jamais deux fois la même valeur. */
const candidatsMulDiv = (it) => {
    const { g, d, cas, res } = it;
    const c = [];
    const pousser = (code, num, den) => { if (num >= 1 && den >= 1) c.push({ code, val: { num, den } }); };
    if (cas === 'produit') {
        pousser('produits_en_croix', g.a * d.b, g.b * d.a);
        const L = (g.b * d.b) / gcd(g.b, d.b);
        pousser('meme_denominateur_garde', (g.a * L / g.b) * (d.a * L / d.b), L);
        pousser('hauts_seuls', g.a * d.a, randomChoice([g.b, d.b]));
        pousser('bas_additionnes', g.a * d.a, g.b + d.b);
    } else {
        pousser('pas_inverse', g.a * d.a, g.b * d.b);
        pousser('inverse_du_mauvais', g.b * d.a, g.a * d.b);
        pousser('les_deux_inverses', g.b * d.b, g.a * d.a);
    }
    const vus = [res];
    return c.filter((x) => {
        if (vus.some((v) => memeValeur(v, x.val))) return false;
        vus.push(x.val);
        return true;
    }).map((x) => ({ ...x, texte: `$${resTex(x.val)}$` }));
};

/** Trois distracteurs de familles distinctes ; null s'il en manque. */
const distracteursMulDiv = (it) => {
    const cands = shuffle(candidatsMulDiv(it));
    const pris = [];
    for (const x of cands) if (!pris.some((y) => y.code === x.code)) pris.push(x);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

/** Un item d'une opération : ± sur un cas de 1054/1097 (tiré si absent), × et ÷ sur leurs items. */
const CAS_PLUS = ['quelconque', 'multiples', 'premiers', 'meme_den'];
const tirerPour = (pour, cas = null) => {
    if (pour === 'produit' || pour === 'quotient') return tirerMulDiv(pour);
    const c = cas || randomChoice(CAS_PLUS);
    return c === 'quelconque' ? tirerQuelconque({ opForce: OP_DE[pour] }) : tirerItem(c, { opForce: OP_DE[pour] });
};
const faussesReponses = (it) => {
    if (!estPlus(it)) return distracteursMulDiv(it);
    return it.cas === 'quelconque' ? distracteursQcmQuelconque(it) : distracteursQcm(it);
};
const candidatsDe = (it) => {
    if (!estPlus(it)) return candidatsMulDiv(it);
    return it.cas === 'quelconque' ? candidatsParFamille(it) : candidats(it);
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

// « irréductible » dit à l'élève que 4/6 ne sera pas compté juste pour 2/3 (retour prof 13/09 : les résultats sont irréductibles par construction, chaque case est jugée comme un nombre)
const CONSIGNE_FLASH = '<p><b>Calcule.</b> Écris le résultat en une seule fraction <b>irréductible</b>.</p>';

const payloadFlash = (pour, items) => {
    const payload = makeTexteTrou(phraseFlash(items), CONSIGNE_FLASH, { ...opts(ATTENDU[pour], L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, sousType: pour, explications: { regle: EXPLICATIONS.regle, bas_additionnes: EXPLICATIONS.bas_additionnes, produits_en_croix: EXPLICATIONS.produits_en_croix, pas_inverse: EXPLICATIONS.pas_inverse } };
    payload._meta.params = { pour, items };
    return payload;
};

/** Quatre items de la même opération, résultats distincts ; en ±, un cas de 1054/1097 par ligne (les quatre cas mélangés). */
const genFlash = (pour) => () => {
    for (let essai = 0; essai < 200; essai++) {
        const cas = shuffle(CAS_PLUS);
        const items = [0, 1, 2, 3].map((i) => tirerPour(pour, cas[i]));
        if (items.some((it) => !it)) continue;
        if (new Set(items.map((it) => `${it.res.num}/${it.res.den}`)).size < 4) continue;
        return payloadFlash(pour, items);
    }
    throw new Error(`gen_1213 : série flash ${pour} sans tirage`);
};

// ─── Carte 2 : Quel résultat ? (QCM) ──────────────────────────────────────

const SOUS_TYPE = { '+': 'somme', '-': 'difference', '×': 'produit', '÷': 'quotient' };

const genQcm = (pour) => () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirerPour(pour, pour === 'somme' || pour === 'difference' ? randomChoice(['quelconque', 'quelconque', 'multiples', 'premiers']) : null);
        if (!it) continue;
        const faux = faussesReponses(it);
        if (!faux) continue;
        const bon = `$${resTex(it.res)}$`;
        const choix = [bon, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>Quel est le résultat de ${tex(exprTex(it))} ?</b></p>`, opts(ATTENDU[pour], L_QCM));
        payload.contenu = { ...payload.contenu, sousType: SOUS_TYPE[it.op], explications: EXPLICATIONS };
        payload._meta.params = { ...it, pour, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error(`gen_1213 : QCM ${pour} sans tirage`);
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

/** Une affirmation d'un item × ou ÷ (la valeur), vraie ou d'une famille tirée. */
const affirmationDe = (it, vraie) => {
    if (estPlus(it)) return it.cas === 'quelconque' ? affirmation(it, vraie, candidatsParFamille(it)) : affirmation(it, vraie);
    if (vraie) return { ligne: tex(`${exprTex(it)} = ${resTex(it.res)}`), code: null };
    const cands = candidatsDe(it);
    if (!cands.length) return null;
    const d = randomChoice(cands);
    return { ligne: tex(`${exprTex(it)} = ${resTex(d.val)}`), code: d.code };
};

/** La ligne du geste ÷ : « g ÷ d = g × (inverse) » vraie, ou fausse par pas_inverse / inverse_du_mauvais ; une fois sur deux le diviseur est un entier. */
const ligneGeste = (vraie) => {
    const g = fractionLibre();
    const entier = randomChoice([true, false]);
    const n = randInt(2, 9);
    const d = entier ? { a: n, b: 1 } : fractionLibre();
    if (!entier && (memeFrac(g, d) || estSacrifie('quotient', g, d))) return null;
    // la ligne affiche aussi un PRODUIT (g × d, g × inverse de d, inverse de g × d) : jamais le couple sacrifié du produit
    const inv = (f) => ({ a: f.b, b: f.a });
    if ([[g, d], [g, inv(d)], [inv(g), d]].some(([u, v]) => estSacrifie('produit', u, v))) return null;
    const dTex = entier ? String(n) : fTex(d);
    // l'inverse de 1/5, c'est 5 : jamais un dénominateur 1 affiché
    const invTex = (f) => (f.a === 1 ? String(f.b) : fracTex(f.b, f.a));
    if (vraie) return { ligne: tex(`${fTex(g)} \\div ${dTex} = ${fTex(g)} \\times ${invTex(d)}`), code: null };
    if (entier || randomChoice([true, false])) return { ligne: tex(`${fTex(g)} \\div ${dTex} = ${fTex(g)} \\times ${dTex}`), code: 'pas_inverse' };
    return { ligne: tex(`${fTex(g)} \\div ${dTex} = ${invTex(g)} \\times ${fTex(d)}`), code: 'inverse_du_mauvais' };
};

/** Quatre lignes de la même opération ; en ÷, la quatrième est la ligne du geste. */
const genVf = (pour) => () => {
    for (let essai = 0; essai < 200; essai++) {
        const nbItems = pour === 'quotient' ? 3 : 4;
        const cas = shuffle(CAS_PLUS);
        const items = Array.from({ length: nbItems }, (_, i) => tirerPour(pour, cas[i]));
        if (items.some((it) => !it)) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const affs = items.map((it, i) => affirmationDe(it, verites[i]));
        if (pour === 'quotient') affs.push(ligneGeste(verites[3]));
        if (affs.some((a) => !a)) continue;
        const lignes = shuffle(affs.map((a, i) => ({ ligne: a.ligne, vraie: verites[i], code: a.code })));
        if (new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), lignes.map((l) => (l.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(ATTENDU[pour], L_VF));
        payload.contenu = { ...payload.contenu, sousType: pour, explications: EXPLICATIONS };
        payload._meta.params = { pour, items, verites: lignes.map((l) => l.vraie), distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error(`gen_1213 : V/F ${pour} sans tirage`);
};
// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix : la clé et trois familles distinctes ; chaque distracteur strictement positif, différent de la clé et des autres EN VALEUR ; collision → nouveau tirage',
        'un fichier, quatre attendus : clés somme_* (1213), difference_* (1214), produit_* (1215), quotient_* (1216) ; contenu.sousType = l\'opération',
        '± : les familles et la sélection de 1054 (cas de 5e) et de 1097 (cas quelconque), importées',
        'sacrifiés : ceux de 1054 et 1097 (importés), (2/3 ; 4/5) et (3/4 ; 5/2) en ×, (3/4 ; 2/5) en ÷',
    ],
    qcm_unique: {
        support: 'a/b et c/d irréductibles non entières, numérateurs 1..9, dénominateurs 2..9, résultat irréductible par construction, termes ≤ 81',
        familles_produit: {
            produits_en_croix: 'ad/bc, la principale (DEPP 2de 2025 q.3 : 2/5 × 2/3 → 6/10, 7,6 %)',
            meme_denominateur_garde: 'les deux fractions au dénominateur commun, numérateurs multipliés, commun gardé (→ 60/15, 6,3 %)',
            hauts_seuls: 'ac sur l\'un des dénominateurs (2/5 × 3/5 → 6/5)',
            bas_additionnes: 'ac/(b + d)',
        },
        familles_quotient: {
            pas_inverse: 'ac/bd, la principale : on multiplie sans retourner',
            inverse_du_mauvais: 'bc/ad : la première fraction retournée',
            les_deux_inverses: 'bd/ac : les deux retournées',
        },
    },
    vf_tableau_vf: 'quatre lignes de la même opération ; en ÷, trois items et la ligne du geste (vraie, ou pas_inverse / inverse_du_mauvais) ; chaque fausse tire sa famille au hasard parmi les candidats de son item',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {};
const categoriesDe = (pour) => {
    GENERATORS[`${pour}_flash_texte_trou_saisie`] = genFlash(pour);
    GENERATORS[`${pour}_resultat_qcm_unique`] = genQcm(pour);
    GENERATORS[`${pour}_sens_tableau_vf`] = genVf(pour);
    return [
        { id: `${pour}_flash`, label: L_FLASH, formatsList: [{ key: `${pour}_flash_texte_trou_saisie`, label: 'Saisie trous' }] },
        { id: `${pour}_resultat`, label: L_QCM, formatsList: [{ key: `${pour}_resultat_qcm_unique`, label: 'QCM' }] },
        { id: `${pour}_sens`, label: L_VF, formatsList: [{ key: `${pour}_sens_tableau_vf`, label: 'Vrai/Faux' }] },
    ];
};
/** 1213 : la somme. */
export const CATEGORIES = categoriesDe('somme');
/** 1214 : la différence (gen_1214). */
export const CATEGORIES_DIFFERENCE = categoriesDe('difference');
/** 1215 : le produit (gen_1215). */
export const CATEGORIES_PRODUIT = categoriesDe('produit');
/** 1216 : le quotient (gen_1216). */
export const CATEGORIES_QUOTIENT = categoriesDe('quotient');
const clesDe = (cats) => cats.flatMap((c) => c.formatsList.map(({ key }) => key));

/** Tire parmi les clés données (dispatch des quatre gens). */
export const genererParmi = (cles, typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = cles.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(cles);
    } else key = randomChoice(cles);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const generate = (typeExercice) => genererParmi(clesDe(CATEGORIES), typeExercice);
export { ATTENDU_ID };
